/**
 * Memorial appearance presets — themes, accents, wallpaper, candle styles.
 */

export const MEMORIAL_THEME_PRESETS = {
  classic: { id: 'classic', label: 'Classic', description: 'Warm forest green and ivory — calm and timeless.' },
  warm_light: { id: 'warm_light', label: 'Warm light', description: 'Soft cream and golden light — gentle and uplifting.' },
  garden: { id: 'garden', label: 'Garden', description: 'Leaf-green tones with natural warmth.' },
  night_sky: { id: 'night_sky', label: 'Night sky', description: 'Deep navy with starlight accents — peaceful and luminous.' },
  heritage: { id: 'heritage', label: 'Heritage', description: 'Archive gold and parchment — dignified and personal.' },
  minimal: { id: 'minimal', label: 'Minimal', description: 'Clean ivory and slate — quiet and refined.' },
};

export const MEMORIAL_ACCENT_PRESETS = {
  deep_green: { id: 'deep_green', label: 'Deep green', swatch: 'hsl(152 28% 32%)' },
  warm_gold: { id: 'warm_gold', label: 'Warm gold', swatch: '#c9a961' },
  soft_rose: { id: 'soft_rose', label: 'Soft rose', swatch: 'hsl(350 28% 58%)' },
  midnight_blue: { id: 'midnight_blue', label: 'Midnight blue', swatch: 'hsl(220 32% 28%)' },
  ivory: { id: 'ivory', label: 'Ivory', swatch: 'hsl(42 32% 88%)' },
  burgundy: { id: 'burgundy', label: 'Burgundy', swatch: 'hsl(350 32% 32%)' },
  lavender: { id: 'lavender', label: 'Lavender', swatch: 'hsl(270 22% 58%)' },
  slate: { id: 'slate', label: 'Slate', swatch: 'hsl(220 10% 42%)' },
};

export const MEMORIAL_WALLPAPER_PRESETS = {
  soft_light: { id: 'soft_light', label: 'Soft light' },
  candle_glow: { id: 'candle_glow', label: 'Candle glow' },
  night_sky: { id: 'night_sky', label: 'Night sky' },
  garden_leaves: { id: 'garden_leaves', label: 'Garden leaves' },
  warm_paper: { id: 'warm_paper', label: 'Warm paper' },
  subtle_clouds: { id: 'subtle_clouds', label: 'Subtle clouds' },
  chapel_texture: { id: 'chapel_texture', label: 'Quiet chapel texture' },
  memory_light: { id: 'memory_light', label: 'Abstract memory light' },
};

export const MEMORIAL_CANDLE_STYLES = {
  classic: { id: 'classic', label: 'Classic candle' },
  warm_glow: { id: 'warm_glow', label: 'Warm glow' },
  lantern: { id: 'lantern', label: 'Lantern light' },
  soft_flame: { id: 'soft_flame', label: 'Soft flame' },
};

export const MEMORIAL_BACKGROUND_STYLES = {
  theme_default: { id: 'theme_default', label: 'Theme default' },
  preset: { id: 'preset', label: 'Preset background' },
  custom_image: { id: 'custom_image', label: 'Custom image' },
};

export const ATMOSPHERE_FIELD_LABELS = {
  favourite_song: 'Their favourite song',
  favourite_scent: 'A scent they loved',
  favourite_food: 'Food or drink everyone remembers',
  favourite_place: 'A place that felt like home',
  favourite_colour: 'A colour that reminds us of them',
  favourite_flower: 'A flower they loved',
  favourite_saying: 'Words they often said',
  favourite_object: 'A treasured object',
  personal_note: 'Personal atmosphere note',
};

export const DEFAULT_MEMORIAL_APPEARANCE = {
  theme_preset: 'classic',
  theme_accent_colour: null,
  background_style: 'theme_default',
  wallpaper_url: '',
  wallpaper_preset: null,
  candle_style: 'classic',
  atmosphere_enabled: false,
  atmosphere: Object.fromEntries(Object.keys(ATMOSPHERE_FIELD_LABELS).map((k) => [k, ''])),
  soundtrack_note: '',
};

const THEME_VARS = {
  classic: {
    '--memorial-forest': '152 28% 16%',
    '--memorial-forest-deep': '152 32% 11%',
    '--memorial-charcoal': '24 12% 14%',
    '--memorial-ivory': '42 32% 96%',
    '--memorial-ivory-deep': '40 24% 92%',
  },
  warm_light: {
    '--memorial-forest': '38 28% 28%',
    '--memorial-forest-deep': '38 32% 22%',
    '--memorial-charcoal': '32 14% 18%',
    '--memorial-ivory': '42 38% 98%',
    '--memorial-ivory-deep': '40 30% 94%',
  },
  garden: {
    '--memorial-forest': '145 26% 22%',
    '--memorial-forest-deep': '148 30% 16%',
    '--memorial-charcoal': '140 12% 16%',
    '--memorial-ivory': '88 28% 96%',
    '--memorial-ivory-deep': '85 22% 92%',
  },
  night_sky: {
    '--memorial-forest': '220 32% 14%',
    '--memorial-forest-deep': '225 36% 10%',
    '--memorial-charcoal': '230 18% 12%',
    '--memorial-ivory': '42 28% 96%',
    '--memorial-ivory-deep': '220 20% 18%',
  },
  heritage: {
    '--memorial-forest': '32 22% 20%',
    '--memorial-forest-deep': '28 26% 14%',
    '--memorial-charcoal': '24 14% 14%',
    '--memorial-ivory': '42 34% 97%',
    '--memorial-ivory-deep': '38 26% 91%',
  },
  minimal: {
    '--memorial-forest': '220 8% 24%',
    '--memorial-forest-deep': '220 10% 18%',
    '--memorial-charcoal': '220 8% 20%',
    '--memorial-ivory': '0 0% 99%',
    '--memorial-ivory-deep': '220 8% 94%',
  },
};

const ACCENT_GOLD = {
  deep_green: '#3d6b52',
  warm_gold: '#c9a961',
  soft_rose: '#c4848a',
  midnight_blue: '#3d4f6b',
  ivory: '#e8e0d0',
  burgundy: '#6b3a42',
  lavender: '#9a8ab8',
  slate: '#5c6470',
};

export function mergeMemorialAppearance(appearance) {
  return {
    ...DEFAULT_MEMORIAL_APPEARANCE,
    ...(appearance || {}),
    atmosphere: {
      ...DEFAULT_MEMORIAL_APPEARANCE.atmosphere,
      ...(appearance?.atmosphere || {}),
    },
  };
}

export function getMemorialAppearanceProps(appearance, resolveWallpaperUrl) {
  const merged = mergeMemorialAppearance(appearance);
  const theme = THEME_VARS[merged.theme_preset] || THEME_VARS.classic;
  const accentId = merged.theme_accent_colour;
  const accentColor = accentId && ACCENT_GOLD[accentId] ? ACCENT_GOLD[accentId] : theme['--memorial-gold'] || '#c9a961';

  const style = {
    ...Object.fromEntries(Object.entries(theme).map(([k, v]) => [k, `hsl(${v})`])),
    '--memorial-gold': accentColor,
  };

  let wallpaperUrl = null;
  if (merged.background_style === 'custom_image' && merged.wallpaper_url) {
    wallpaperUrl = resolveWallpaperUrl?.(merged.wallpaper_url) || merged.wallpaper_url;
  }

  return {
    'data-memorial-theme': merged.theme_preset,
    'data-memorial-accent': accentId || '',
    'data-memorial-candle': merged.candle_style,
    'data-memorial-wallpaper': merged.background_style === 'preset' ? (merged.wallpaper_preset || '') : '',
    style,
    wallpaperUrl,
    appearance: merged,
  };
}

export function getDefaultAppearanceForm(memorial = {}) {
  const atm = memorial.atmosphere || {};
  return {
    theme_preset: memorial.theme_preset || 'classic',
    theme_accent_colour: memorial.theme_accent_colour || '',
    background_style: memorial.background_style || 'theme_default',
    wallpaper_url: memorial.wallpaper_url || '',
    wallpaper_preset: memorial.wallpaper_preset || '',
    candle_style: memorial.candle_style || 'classic',
    cover_photo_url: memorial.cover_photo_url || '',
    portrait_photo_url: memorial.portrait_photo_url || '',
    soundtrack_enabled: Boolean(memorial.soundtrack_enabled),
    soundtrack_title: memorial.soundtrack_title || '',
    soundtrack_url: memorial.soundtrack_url || '',
    soundtrack_note: memorial.soundtrack_note || '',
    atmosphere_enabled: Boolean(memorial.atmosphere_enabled),
    atmosphere: Object.fromEntries(
      Object.keys(ATMOSPHERE_FIELD_LABELS).map((k) => [k, atm[k] || '']),
    ),
  };
}

export function appearancePatchPayload(form) {
  const atmosphere = {};
  Object.keys(ATMOSPHERE_FIELD_LABELS).forEach((k) => {
    const v = (form.atmosphere?.[k] || '').trim();
    atmosphere[k] = v || null;
  });
  return {
    theme_preset: form.theme_preset,
    theme_accent_colour: form.theme_accent_colour || null,
    background_style: form.background_style,
    wallpaper_url: form.wallpaper_url || null,
    wallpaper_preset: form.wallpaper_preset || null,
    candle_style: form.candle_style,
    cover_photo_url: form.cover_photo_url || null,
    portrait_photo_url: form.portrait_photo_url || null,
    atmosphere_enabled: form.atmosphere_enabled,
    atmosphere,
  };
}

export const DEFAULT_APPEARANCE_RESET = {
  theme_preset: 'classic',
  theme_accent_colour: null,
  background_style: 'theme_default',
  wallpaper_url: null,
  wallpaper_preset: null,
  candle_style: 'classic',
  atmosphere_enabled: false,
  atmosphere: Object.fromEntries(Object.keys(ATMOSPHERE_FIELD_LABELS).map((k) => [k, null])),
};
